import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import CourseTable from '../../../components/tables/CourseTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useCourseSearch, useDeleteCourse } from '../../../hooks/useCourseApi';
import { useCountries } from '../../../hooks/useCountryApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import { CourseDTO, CourseSearchRequestDTO } from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';

const ACTIVE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

const CourseList: React.FC = () => {
  const [formFilters, setFormFilters] = useState<Partial<CourseSearchRequestDTO>>({});
  const [searchFilters, setSearchFilters] = useState<Partial<CourseSearchRequestDTO>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [items, setItems] = useState<CourseDTO[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const { search } = useCourseSearch();
  const deleteCourse = useDeleteCourse();
  const { data: countries = [] } = useCountries();

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Courses', href: '#' },
  ];

  const totalPages = Math.ceil(totalRecords / pageSize);

  const countryOptions = countries
    .filter((c) => c.id)
    .map((c) => ({ value: c.id as string, label: c.name || '' }));

  const fetchItems = async () => {
    setIsLoading(true);
    try {
      const response = await search({
        page: currentPage,
        pageSize,
        sortBy: 'Name',
        sortDescending: false,
        filters: searchFilters,
      });
      setItems(response.results);
      setTotalRecords(response.totalCount);
    } catch (error) {
      console.error('Error fetching Courses:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [currentPage, pageSize, searchFilters]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    setSearchFilters(formFilters);
  };

  const handleClearSearch = () => {
    setFormFilters({});
    setSearchFilters({});
    setCurrentPage(1);
  };

  const handleDelete = async (dto: CourseDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await deleteCourse.mutateAsync(dto.id || '');
      await fetchItems();
    } catch (error) {
      console.error('Error deleting Course:', error);
    }
  };

  const rows = items.map((course) => ({
    ...course,
    countryName: countries.find((c) => c.id === course.countryId)?.name || '',
  }));

  return (
    <AdminLayout
      activeMenuItem="/admin/courses"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Settings.Course.View"
    >
      <Card
        title="Manage Courses"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={<AddNewButton onClick={() => navigate('/admin/courses/create')} />}
      >
        <form onSubmit={handleSearch} className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <FormField
              id="name"
              label="Search by Name"
              value={formFilters.name || ''}
              onChange={(e) => setFormFilters({ ...formFilters, name: e.target.value })}
              placeholder="Search by Name"
            />
            <FormField
              id="address"
              label="Search by Address"
              value={formFilters.address || ''}
              onChange={(e) => setFormFilters({ ...formFilters, address: e.target.value })}
              placeholder="Search by Address"
            />
            <FormField
              id="countryId"
              label="Country"
              type="dropdown"
              value={formFilters.countryId || ''}
              onChange={(e) => setFormFilters({ ...formFilters, countryId: e.target.value || undefined })}
              options={countryOptions}
              placeholder="All Countries"
            />
            <FormField
              id="active"
              label="Active"
              type="dropdown"
              value={formFilters.active === undefined ? '' : String(formFilters.active)}
              onChange={(e) =>
                setFormFilters({
                  ...formFilters,
                  active: e.target.value === '' ? undefined : e.target.value === 'true',
                })
              }
              options={ACTIVE_OPTIONS}
            />
          </div>

          <div className="flex space-x-4 mb-4">
            <Button variant="secondary" onClick={handleClearSearch}>
              Clear Search
            </Button>
            <Button type="submit">Search</Button>
          </div>
        </form>

        <CourseTable
          data={rows}
          handleEdit={(dto) => navigate(`/admin/courses/${dto.id}`)}
          handleDelete={handleDelete}
          currentPage={currentPage}
          totalPages={totalPages}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default CourseList;
